import { useRef, useState, useEffect } from 'react';
import Image from 'next/image';
import classes from './product-view.module.scss';
import Heading from '@next-app/components/elements/Heading/Heading';
import Conditional from '@next-app/components/Conditional';
import { AudioModalData } from '@next-app/interface/PDP/PDPProductView';

const getMainPlayList = (data: any) => {
  return data && data.mainPlayList ? data.mainPlayList : null;
};

const ProductAudioModal = ({
  audioInfo,
  autoPlay,
  stopAudio,
  handleStopAudio,
}: AudioModalData) => {
  const mainPlayList = getMainPlayList(audioInfo);
  const { playlistImgURL, playlistName, audioTracks } = mainPlayList;

  const audioRef = useRef<HTMLAudioElement>(null);

  const [currentSongIndex, setCurrentSongIndex] = useState(0);

  const currentSong = audioTracks[currentSongIndex].audioTrackURL;
  const currentSongTitle = audioTracks[currentSongIndex].audioTrackName;
  const currentSongLyrics = audioTracks[currentSongIndex].lyrics;

  useEffect(() => {
    const audioModal = document.getElementById('audioModal');
    if (audioModal) {
      const handleBackdropClick = (event: any) => {
        if (event.target === audioModal) {
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
          handleStopAudio();
        }
      };
      audioModal.addEventListener('click', handleBackdropClick);
      return () => {
        audioModal.removeEventListener('click', handleBackdropClick);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (autoPlay && audioRef.current) {
      audioRef.current.load();
      audioRef.current.play();
    } else if (audioRef.current) {
      audioRef.current.load(); // Ensure the new track is loaded even if not autoplaying
    }
  }, [autoPlay, currentSong]);

  useEffect(() => {
    if (stopAudio && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [stopAudio]);

  const handlePreviousClick = () => {
    setCurrentSongIndex((prevIndex) => {
      const newIndex = prevIndex === 0 ? audioTracks.length - 1 : prevIndex - 1;
      setAutoplayForTrackChange();
      return newIndex;
    });
  };

  const handleNextClick = () => {
    setCurrentSongIndex((prevIndex) => {
      const newIndex = (prevIndex + 1) % audioTracks.length;
      setAutoplayForTrackChange();
      return newIndex;
    });
  };

  // Helper to trigger autoplay for track change
  const setAutoplayForTrackChange = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.load();
        audioRef.current.play();
      }
    }, 0);
  };

  return (
    <div>
      <div className={`${classes['media-container']}`}>
        <audio ref={audioRef} controls data-testid="audio-element">
          <source src={currentSong} type="audio/ogg" />
          <source src={currentSong} type="audio/mpeg" />
        </audio>
        <div className={`${classes['previous-next-container']}`}>
          <span
            data-testid="previous-button"
            className={`ls-icon ${classes['arrow-previous']}`}
            onClick={handlePreviousClick}
          ></span>
          <span
            data-testid="next-button"
            className={`ls-icon ${classes['arrow-next']}`}
            onClick={handleNextClick}
          >
            {' '}
          </span>
        </div>
      </div>
      <div>
        <Heading
          customClass={`fw-bold ll-fs-18 ${classes['audio-playlist-title']}`}
          tagName="3"
        >
          All Playlists
        </Heading>
        <span className={`fw-bold d-block ${classes['audio-playlist-name']}`}>
          {playlistName}
        </span>
        <div className="row">
          <div className={`col-md-6 ${classes['audio-playlist-image']}`}>
            <Image
              src={playlistImgURL}
              alt="Audio image"
              width={180}
              height={135}
              quality={100}
              unoptimized={true}
            />
          </div>
          <div className={`col-md-6 ${classes['lyrics-container']}`}>
            <Heading
              tagName="4"
              customClass={`fw-bold fs-sm ${classes['lyrics-title']} d-block d-md-none`}
            >
              {playlistName}{' '}
            </Heading>
            <div className={`position-relative ${classes['lyrics-wrapper']}`}>
              <Heading
                tagName="4"
                customClass={`fw-bold fs-sm ${classes['lyrics-title-mid']}`}
              >
                Lyrics
              </Heading>

              <div className={`${classes['lyrics-wrapper-content']}`}>
                <Heading
                  tagName="4"
                  customClass={`fw-bold ll-fs-14 ${classes['lyrics-title']}`}
                >
                  {currentSongTitle}
                </Heading>
                <div dangerouslySetInnerHTML={{ __html: currentSongLyrics }} />
              </div>
            </div>
          </div>
        </div>
        <div className="songListContainer">
          <Heading
            tagName="4"
            customClass={`fw-bold fs-sm ${classes['lyrics-title']} d-none d-md-block`}
          >
            {playlistName}{' '}
          </Heading>

          <div className={`ll-link-cursor ${classes['song-list']}`}>
            {audioTracks.map((data: any, index: any) => (
              <p
                key={data?.audioTrackNumber}
                onClick={() => setCurrentSongIndex(index)}
                style={{ cursor: 'pointer' }}
                className="ll-fs-14"
              >
                <Conditional if={currentSongIndex === index}>
                  <i
                    className={`ls-icon position-relative ${classes['icn-play']}`}
                  ></i>
                </Conditional>

                <span className="light">{data?.audioTrackName}</span>
              </p>
            ))}
          </div>
        </div>
        <div className={`ll-link-cursor ${classes['audio-modal-footer']}`}>
          <i
            className={`ls-icon ${classes['icn-x-close-lgt']}`}
            aria-hidden="true"
          ></i>
          <span
            className="light ll-fs-14"
            data-bs-dismiss="modal"
            onClick={handleStopAudio}
          >
            Close Audio Sample
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProductAudioModal;
